import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Popover,
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutline,
  KeyboardArrowDownRounded,
} from "@mui/icons-material";
import type { IStreams } from "../../../types/streams";
import type { FrontendStatus } from "../../../types/bookings";

const STATUS_OPTIONS: FrontendStatus[] = [
  "enquiry",
  "confirmed",
  "fulfilled",
  "cancelled",
  "lost",
  "testing",
];

const getStatusChipStyles = (status: string) => {
  switch (status.toLowerCase()) {
    case "confirmed":
      return {
        backgroundColor: "#C8E6C9",
        color: "#2E7D32",
        label: "Confirmed",
      };
    case "fulfilled":
    case "completed":
      return {
        backgroundColor: "#C5CAE9",
        color: "#283593",
        label: "Fulfilled",
      };
    case "enquiry":
    case "pending":
      return { backgroundColor: "#FFE0B2", color: "#E65100", label: "Enquiry" };
    case "cancelled":
      return {
        backgroundColor: "#FFCDD2",
        color: "#C62828",
        label: "Cancelled",
      };
    case "lost":
      return { backgroundColor: "#EEEEEE", color: "#616161", label: "Lost" };
    case "testing":
      return { backgroundColor: "#D7CCC8", color: "#4E342E", label: "Testing" };
    default:
      return { backgroundColor: "#E0E0E0", color: "#424242", label: status };
  }
};

interface StatusDropdownProps {
  streamId: string;
  currentStatus: string;
  onStatusChange?: (id: string, newStatus: FrontendStatus) => void;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  streamId,
  currentStatus,
  onStatusChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  const handleSelect = (e: React.MouseEvent, status: FrontendStatus) => {
    e.stopPropagation();
    onStatusChange?.(streamId, status);
    setAnchorEl(null);
  };

  const currentStyles = getStatusChipStyles(currentStatus);

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          px: "12px",
          py: "5px",
          borderRadius: "999px",
          backgroundColor: currentStyles.backgroundColor,
          color: currentStyles.color,
          cursor: "pointer",
          fontWeight: 600,
          fontSize: "12px",
          userSelect: "none",
          transition: "filter 0.15s ease",
          "&:hover": { filter: "brightness(0.95)" },
        }}
      >
        {currentStyles.label}
        <KeyboardArrowDownRounded
          sx={{
            fontSize: "16px",
            transition: "transform 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(e) => handleClose(e as React.MouseEvent)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              mt: "6px",
              borderRadius: "16px",
              p: "10px",
              minWidth: "180px",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        {STATUS_OPTIONS.map((status) => {
          const styles = getStatusChipStyles(status);
          const isSelected = status === currentStatus.toLowerCase();

          return (
            <Box
              key={status}
              onClick={(e) => handleSelect(e, status)}
              sx={{
                px: "16px",
                py: "8px",
                borderRadius: "999px",
                backgroundColor: styles.backgroundColor,
                color: styles.color,
                fontWeight: isSelected ? 700 : 500,
                fontSize: "13px",
                textAlign: "center",
                cursor: "pointer",
                outline: isSelected ? `2px solid ${styles.color}` : "none",
                outlineOffset: "-2px",
                transition: "filter 0.15s ease, transform 0.1s ease",
                "&:hover": {
                  filter: "brightness(0.93)",
                  transform: "scale(1.02)",
                },
              }}
            >
              {styles.label}
            </Box>
          );
        })}
      </Popover>
    </>
  );
};

interface StreamListTable2Props {
  streams: IStreams[];
  loading: boolean;
  onRowClick: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onStatusChange?: (id: string, newStatus: FrontendStatus) => void;
  hasEditPermission: boolean;
  hasDeletePermission: boolean;
}

const StreamListTable2: React.FC<StreamListTable2Props> = ({
  streams,
  loading,
  onRowClick,
  onEdit,
  onDelete,
  onStatusChange,
  hasEditPermission,
  hasDeletePermission,
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress sx={{ color: "#4285F4" }} />
      </Box>
    );
  }

  if (streams.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          No streams found
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ borderRadius: "12px", overflow: "hidden" }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
            <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
              Stream Title
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
              Date
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, color: "text.primary", width: 200 }}
            >
              Location
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
              Status
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {streams.map((stream) => {
            const [venue, ...addressParts] = (
              stream.serviceAddress || ""
            ).split(",");
            const address = addressParts.join(", ").trim();

            return (
              <TableRow
                key={stream.id}
                hover
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "background.paper" },
                }}
                onClick={() => onRowClick(stream.id)}
              >
                <TableCell sx={{ width: 200, maxWidth: 200 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {(stream.title || "").replace(/^Stream for\s*/i, "")}
                  </Typography>
                </TableCell>

                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {stream.serviceDate}
                  </Typography>
                </TableCell>

                <TableCell sx={{ width: 300, maxWidth: 300 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {venue || "No Venue"}
                    </Typography>
                    {address && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {address}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                <TableCell onClick={(e) => e.stopPropagation()}>
                  <StatusDropdown
                    streamId={stream.id}
                    currentStatus={stream.status || "enquiry"}
                    onStatusChange={onStatusChange}
                  />
                </TableCell>

                <TableCell>
                  <Box
                    sx={{ display: "flex", gap: 0.5 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {hasEditPermission && (
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(stream.id)}
                          sx={{ color: "text.secondary" }}
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasDeletePermission && (
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(stream.id)}
                          sx={{ color: "#F44336" }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StreamListTable2;
